import { inject, Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import {
  TrialInsightsRemainingSpendResponseType,
  TrialInsightsResponseGeneric,
} from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';
import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { EntityType, GqlService, PeriodType } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';

type Response = TrialInsightsResponseGeneric<ResponseType[]>;
type ResponseType = TrialInsightsRemainingSpendResponseType;

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsRemainingSpendStoreService {
  private gqlService = inject(GqlService);
  private query = inject(TrialInsightsQuery);
  private store = inject(TrialInsightsStore);

  getRemainingSpend$(): Observable<unknown> {
    return this.query.select('currentOpenMonth').pipe(
      filter((currentOpenMonth) => !!currentOpenMonth),
      switchMap((currentOpenMonth) => {
        return this.gqlService.getRemainingSpend$({
          entity_type: EntityType.ORGANIZATION,
          period_type: PeriodType.QUARTER,
          start_date: currentOpenMonth || '',
        });
      }),
      this.processResponse()
    );
  }

  getTotalRemainingSpend(spends: Response['data']): number {
    return spends?.reduce((accum, spend) => accum + spend.remaining_spend.amount, 0) || 0;
  }

  processResponse() {
    const processFn = (response: Response, subscriber: Subscriber<unknown>) => {
      const { data } = response;

      this.store.update((state) => {
        return {
          ...state,
          remainingSpend: {
            ...state.remainingSpend,
            data: data ? data : [],
            isLoading: false,
            isLoadingRemaining: false,
            totalRemainingSpend: this.getTotalRemainingSpend(data),
          },
        };
      });

      subscriber.next(data);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }
}
